import React, { useState, useEffect } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { hotjar } from 'react-hotjar';

import withVersion from 'HOC/withVersion/withVersion';
import withOptimizelyFF, {
  HOTJAR_DECISIONS,
} from 'HOC/withOptimizely/withOptimizelyFF';
import { selectCampaignEndDate } from 'services/reducers/campaignReducer';
import { selectPageTrackingFullData } from 'selectors/trackingSelector';
import {
  openInviteModal as openInviteModalAction,
  closeInviteModal as closeInviteModalAction,
} from 'services/actions/socialEcommActions';
import { VIEW_SIZE } from 'utils/device';
import { VERSION } from 'utils/campaign';
import env from 'utils/env';

import useInView from 'hooks/useInView';
import useFacebookScript from 'hooks/useFacebookScript';
import TeamWidget from 'shared-components/TeamWidget/TeamWidget';
import ErrorBoundary from 'shared-components/ErrorBoundary/ErrorBoundary';
import InviteModal from 'shared-components/InviteModal/InviteModal';
import LearnMoreModal from 'shared-components/LearnMoreModal/LearnMoreModal';
import {
  showSuccessToast,
  showErrorToast,
} from 'shared-components/BaseToast/Toast';
import { btnLocationEnum } from 'utils/segment';
import { EVERGREEN_SHOP_BANER_IMAGES } from 'utils/cdnImages';
import Products from '../Products/Products/Products';
import SecondaryHeader from '../Headers/SecondaryHeader/SecondaryHeader';
import TertiaryHeader from '../Headers/TertiaryHeader/TertiaryHeader';
import SaleBar from '../Headers/SaleBar/SaleBar';
import Banner from '../Headers/Banner/Banner';
import SocialBanner from '../Headers/SocialBanner/SocialBanner';
import IntroProductBanner from '../Headers/IntroProductBanner/IntroProductBanner';
import ProductsError from './ProductsError/ProductsError';
import EvergreenShopBanner from './EvergreenShopBanner/EvergreenShopBanner';

const Sale = ({
  viewSize,
  version,
  campaignCurrentWindowEndDate,
  campaignWindowTwoEndDate,
  bannerTitle,
  bannerParagraph,
  bannerBackgroundColor,
  bannerFAQUrl,
  bannerDesktopImageUrl,
  displayedProductsCount,
  productsRequestFailed,
  displaySocialBanner,
  planCode,
  teamId,
  trackingData,
  socialEcommFF,
  isSocialEcomm,
  optimizelyHotjar,
  openInviteModal,
  closeInviteModal,
  loggedIn,
}) => {
  const [isLearnMoreVisible, setIsLearnMoreVisible] = useState(false);
  const { isInView: socialBannerIsInView, elementRef: socialBannerElementRef } =
    useInView();
  const displayTeamWidget = teamId && socialEcommFF;
  const isAddOnsPage = version === VERSION['add-ons'];
  const isEditPage = version === VERSION.edit;
  const isShopPage = version === VERSION.shop;
  const urlParams = new URLSearchParams(window.location.search);
  const inviteOpen = urlParams.get('inviteOpen');
  const location = useLocation();
  useFacebookScript();

  const showLearnMorePopup = () => setIsLearnMoreVisible(true);
  const handleInviteSent = (emailSent) => {
    closeInviteModal();
    if (emailSent)
      showSuccessToast(
        'Your Invite Has Been Sent',
        'You’ve successfully invited your friend to shop with you!'
      );
  };
  const handleInviteError = (title) => showErrorToast(title);

  useEffect(() => {
    if (inviteOpen && isEditPage && socialEcommFF) {
      openInviteModal(btnLocationEnum.friendsCart);
    }
  }, [location]);

  // initialize hotjar if FF is ON and variation is ON
  useEffect(() => {
    if (optimizelyHotjar) {
      const { enabled, variationName } = optimizelyHotjar;
      const { hotjarID, hotjarSV } = env;
      if (
        enabled &&
        variationName === HOTJAR_DECISIONS.on &&
        hotjarID &&
        hotjarSV
      ) {
        hotjar.initialize(hotjarID, hotjarSV);
      }
    }
  }, [optimizelyHotjar]);

  return (
    <>
      <ErrorBoundary>
        {(viewSize === VIEW_SIZE.xLarge || viewSize === VIEW_SIZE.large) && (
          <SecondaryHeader
            version={version}
            campaignEndDate={campaignCurrentWindowEndDate}
            viewSize={viewSize}
          />
        )}
        {/* TODO: create a component to handle the banners logic */}
        {isShopPage && loggedIn && (
          <ErrorBoundary>
            <EvergreenShopBanner
              desktopImage={EVERGREEN_SHOP_BANER_IMAGES.desktopImage}
              tabletImage={EVERGREEN_SHOP_BANER_IMAGES.tabletImage}
              mobileImage={EVERGREEN_SHOP_BANER_IMAGES.mobileImage}
              backgroundColor="#dea89e"
              viewSize={viewSize}
            />
          </ErrorBoundary>
        )}
        {isShopPage && !loggedIn && (
          <IntroProductBanner
            title={bannerTitle}
            paragraph={bannerParagraph}
            backgroundColor={bannerBackgroundColor}
            desktopImageUrl={bannerDesktopImageUrl}
            viewSize={viewSize}
          />
        )}
        {(isAddOnsPage || isEditPage) && (
          <Banner
            title={bannerTitle}
            paragraph={bannerParagraph}
            backgroundColor={bannerBackgroundColor}
            faqUrl={bannerFAQUrl}
            desktopImageUrl={bannerDesktopImageUrl}
            campaignEndDate={campaignCurrentWindowEndDate}
            version={version}
            viewSize={viewSize}
            onClickLearnMore={showLearnMorePopup}
          />
        )}
        {displaySocialBanner && (
          <SocialBanner
            planCode={planCode}
            openInviteModal={openInviteModal}
            viewSize={viewSize}
            isInView={socialBannerIsInView}
            elementRef={socialBannerElementRef}
            displayTeamWidget={displayTeamWidget}
            trackingData={trackingData}
          />
        )}
        {(viewSize === VIEW_SIZE.large || viewSize === VIEW_SIZE.xLarge) && (
          <TertiaryHeader viewSize={viewSize} />
        )}
        {(viewSize === VIEW_SIZE.small || viewSize === VIEW_SIZE.medium) && (
          <SaleBar />
        )}
      </ErrorBoundary>
      <LearnMoreModal
        campaignCurrentWindowEndDate={campaignCurrentWindowEndDate}
        campaignWindowTwoEndDate={campaignWindowTwoEndDate}
        setVisibility={setIsLearnMoreVisible}
        isVisible={isLearnMoreVisible}
        viewSize={viewSize}
        version={version}
        isSocialEcomm={isSocialEcomm}
        planCode={planCode}
      />
      {productsRequestFailed && displayedProductsCount === 0 ? (
        <ProductsError />
      ) : (
        <ErrorBoundary fallbackUI={<ProductsError />}>
          <Products
            displaySocialBanner={displaySocialBanner}
            isLearnMoreVisible={isLearnMoreVisible}
            isEditPage={isEditPage}
            isShopPage={isShopPage}
          />
        </ErrorBoundary>
      )}
      {socialEcommFF && (
        <InviteModal
          onInviteSent={handleInviteSent}
          onInviteError={handleInviteError}
        />
      )}
      {displayTeamWidget && (
        <TeamWidget
          socialBannerIsInView={
            displaySocialBanner ? socialBannerIsInView : true
          }
          openInviteModal={openInviteModal}
          viewSize={viewSize}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  campaignCurrentWindowEndDate: selectCampaignEndDate(state),
  campaignWindowTwoEndDate: state.campaignState.endDate,
  bannerTitle: state.campaignState.bannerTitle,
  bannerParagraph: state.campaignState.bannerParagraph,
  bannerBackgroundColor: state.campaignState.bannerBackgroundColor,
  bannerFAQUrl: state.campaignState.bannerFAQUrl,
  bannerDesktopImageUrl: state.campaignState.bannerDesktopImageUrl,
  displayedProductsCount: state.productsState.displayedProductIds.length,
  productsRequestFailed: state.productsState.productsRequestFailed,
  viewSize: state.viewState.viewSize,
  teamId: state.socialEcommState.team.teamId,
  trackingData: selectPageTrackingFullData(state),
  inviteModalExperience: state.socialEcommState.inviteModal.experience,
  inviteModalProduct: state.socialEcommState.inviteModal.product,
  loggedIn: state.userState.loggedIn,
});

const mapDispatchToProps = {
  openInviteModal: openInviteModalAction,
  closeInviteModal: closeInviteModalAction,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withVersion,
  withOptimizelyFF
)(Sale);
